import React, { useState, useEffect } from 'react';
import { getRecommendations } from '../services/api';
import RecommendationCard from '../components/RecommendationCard';
import { Target, TrendingDown, TrendingUp, Minus, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const RecommendationsPage = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [energyFilter, setEnergyFilter] = useState('all');

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const response = await getRecommendations();
      setRecommendations(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des recommandations');
    } finally {
      setLoading(false);
    }
  };

  const filtered = recommendations.filter(r => {
    if (filter !== 'all' && r.label !== filter) return false;
    if (energyFilter !== 'all' && r.energy_type !== energyFilter) return false;
    return true;
  });

  const counts = {
    favorable: recommendations.filter(r => r.label === 'favorable').length,
    neutral: recommendations.filter(r => r.label === 'neutral').length,
    unfavorable: recommendations.filter(r => r.label === 'unfavorable').length,
  };

  const avgScore = recommendations.length > 0
    ? Math.round(recommendations.reduce((sum, r) => sum + r.score, 0) / recommendations.length)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#397DFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recommendations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#191D1E] flex items-center gap-2">
            <Target className="w-6 h-6 text-[#397DFF]" />
            Recommandations
          </h1>
          <p className="text-[#687175] mt-1">Analyse du marché pour vos contrats suivis</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-[#BFC6CC]/12 p-4 text-center">
          <p className="text-3xl font-bold text-[#397DFF]">{avgScore}</p>
          <p className="text-sm text-[#687175]">Score moyen</p>
        </div>
        <div className="bg-white rounded-2xl border border-emerald-200 p-4 text-center cursor-pointer hover:bg-emerald-50 transition-colors"
          onClick={() => setFilter(filter === 'favorable' ? 'all' : 'favorable')}
          data-testid="filter-favorable"
        >
          <p className="text-3xl font-bold text-emerald-600">{counts.favorable}</p>
          <p className="text-sm text-[#687175] flex items-center justify-center gap-1"><TrendingDown className="w-3.5 h-3.5" /> Favorables</p>
        </div>
        <div className="bg-white rounded-2xl border border-amber-200 p-4 text-center cursor-pointer hover:bg-amber-50 transition-colors"
          onClick={() => setFilter(filter === 'neutral' ? 'all' : 'neutral')}
          data-testid="filter-neutral"
        >
          <p className="text-3xl font-bold text-amber-600">{counts.neutral}</p>
          <p className="text-sm text-[#687175] flex items-center justify-center gap-1"><Minus className="w-3.5 h-3.5" /> Neutres</p>
        </div>
        <div className="bg-white rounded-2xl border border-red-200 p-4 text-center cursor-pointer hover:bg-red-50 transition-colors"
          onClick={() => setFilter(filter === 'unfavorable' ? 'all' : 'unfavorable')}
          data-testid="filter-unfavorable"
        >
          <p className="text-3xl font-bold text-red-500">{counts.unfavorable}</p>
          <p className="text-sm text-[#687175] flex items-center justify-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Défavorables</p>
        </div>
      </div>

      {/* Energy type filter */}
      <div className="flex gap-2 items-center">
        <Filter className="w-4 h-4 text-[#687175]" />
        <div className="flex gap-2">
          {[
            { val: 'all', label: 'Tous' },
            { val: 'electricity', label: 'Électricité' },
            { val: 'gas', label: 'Gaz' },
          ].map(f => (
            <button
              key={f.val}
              onClick={() => setEnergyFilter(f.val)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                energyFilter === f.val
                  ? 'bg-[#397DFF] text-white'
                  : 'bg-white text-[#687175] border border-[#BFC6CC]/12 hover:bg-[#F5F6F9]'
              }`}
              data-testid={`energy-filter-${f.val}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {(filter !== 'all' || energyFilter !== 'all') && (
          <button
            onClick={() => { setFilter('all'); setEnergyFilter('all'); }}
            className="text-xs text-[#397DFF] hover:underline ml-2"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Recommendations grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((rec, i) => (
          <RecommendationCard key={rec.contract_name} rec={rec} index={i} />
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#BFC6CC]/12">
          <Target className="w-12 h-12 text-[#BFC6CC] mx-auto mb-3" />
          <p className="text-[#687175]">Aucune recommandation pour ces filtres</p>
        </div>
      )}
    </div>
  );
};

export default RecommendationsPage;
